import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  SeekerComparisonService,
  FavoriteItem,
  CompareUnit,
} from '../../../services/seeker-comparison.service';
import { TranslatePipe } from '../../../../../../core/pipes/translate.pipe';
import { LanguageService } from '../../../../../../core/services/language.service';

export interface ParsedAvailability {
  type: 'range' | 'now' | 'unavailable' | 'single' | 'text' | 'empty';
  label: string;
  startDate?: string;
  endDate?: string;
  duration?: string;
}

@Component({
  selector: 'app-comparison',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './comparison.html',
  styleUrl: './comparison.scss',
})
export class Comparison implements OnInit {
  favorites: FavoriteItem[] = [];
  selected: FavoriteItem[] = [];
  comparisonData: CompareUnit[] = [];

  isLoadingFavorites = false;
  isLoadingComparison = false;
  errorMessage = '';
  showComparison = false;

  readonly MAX = 3;
  private availabilityCache = new Map<string, ParsedAvailability>();

  constructor(
    private svc: SeekerComparisonService,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    this.loadFavorites();
  }

  loadFavorites(): void {
    this.isLoadingFavorites = true;
    this.errorMessage = '';
    this.svc.getFavorites().subscribe({
      next: (res) => {
        this.favorites = (res.data ?? []).map((item) => ({
          ...item,
          itemId: item.itemId || item.favoriteId,
        }));
        this.isLoadingFavorites = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load favorites';
        this.isLoadingFavorites = false;
      },
    });
  }

  toggle(item: FavoriteItem): void {
    const idx = this.selected.findIndex((u) => u.itemId === item.itemId);
    if (idx > -1) {
      this.selected.splice(idx, 1);
    } else if (this.canSelect(item)) {
      this.selected.push(item);
    }
  }

  isSelected(item: FavoriteItem): boolean {
    return this.selected.some((u) => u.itemId === item.itemId);
  }

  canSelect(item: FavoriteItem): boolean {
    if (this.isSelected(item)) return true;
    if (this.selected.length >= this.MAX) return false;
    if (this.selected.length > 0 && this.selected[0].priceType !== item.priceType) return false;
    return true;
  }

  compare(): void {
    if (this.selected.length < 2) return;
    this.isLoadingComparison = true;
    this.errorMessage = '';
    this.svc.compareUnits(this.selected.map((u) => u.itemId)).subscribe({
      next: (res) => {
        this.comparisonData = res.data ?? [];
        this.showComparison = true;
        this.isLoadingComparison = false;
      },
      error: () => {
        this.errorMessage = 'Failed to compare properties';
        this.isLoadingComparison = false;
      },
    });
  }

  back(): void {
    this.showComparison = false;
    this.comparisonData = [];
  }

  clear(): void {
    this.selected = [];
  }

  get activePriceType(): string {
    return this.selected.length > 0 ? this.selected[0].priceType : '';
  }

  get isArabic(): boolean {
    return this.languageService.getCurrentLanguage() === 'ar';
  }

  getAvailabilityInfo(val?: string | null): ParsedAvailability {
    if (!val || !val.trim()) {
      return { type: 'empty', label: '-' };
    }
    const key = `${this.isArabic ? 'ar' : 'en'}_${val}`;
    if (this.availabilityCache.has(key)) {
      return this.availabilityCache.get(key)!;
    }
    const parsed = this.parseAvailability(val);
    this.availabilityCache.set(key, parsed);
    return parsed;
  }

  private parseAvailability(val: string): ParsedAvailability {
    const trimmed = val.trim();
    const isAr = this.isArabic;
    const locale = isAr ? 'ar-u-nu-latn' : 'en-US';

    // 1. Range match (e.g., "Mon Aug 24 2026 ... - Tue Sep 01 2026 ...")
    const rangeSeparators = [' - ', ' to ', ' إلى '];
    for (const sep of rangeSeparators) {
      if (trimmed.includes(sep)) {
        const parts = trimmed.split(sep).map((p) => p.trim());
        if (parts.length === 2) {
          const d1 = new Date(parts[0]);
          const d2 = new Date(parts[1]);
          if (!isNaN(d1.getTime()) && !isNaN(d2.getTime())) {
            const formatOptions: Intl.DateTimeFormatOptions = {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            };
            const startFormatted = new Intl.DateTimeFormat(locale, formatOptions).format(d1);
            const endFormatted = new Intl.DateTimeFormat(locale, formatOptions).format(d2);

            const diffMs = Math.abs(d2.getTime() - d1.getTime());
            const diffDays = Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)));

            let duration = '';
            if (isAr) {
              if (diffDays === 1) duration = 'يوم واحد';
              else if (diffDays === 2) duration = 'يومان';
              else if (diffDays >= 3 && diffDays <= 10) duration = `${diffDays} أيام`;
              else duration = `${diffDays} يوماً`;
            } else {
              duration = diffDays === 1 ? '1 day' : `${diffDays} days`;
            }

            return {
              type: 'range',
              label: isAr ? 'متاح للحجز' : 'Available for booking',
              startDate: startFormatted,
              endDate: endFormatted,
              duration,
            };
          }
        }
      }
    }

    // 2. Single Date check
    if (/\d{4}/.test(trimmed)) {
      const singleDate = new Date(trimmed);
      if (!isNaN(singleDate.getTime())) {
        const formatOptions: Intl.DateTimeFormatOptions = {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        };
        return {
          type: 'single',
          label: isAr ? 'متاح بدءاً من' : 'Available from',
          startDate: new Intl.DateTimeFormat(locale, formatOptions).format(singleDate),
        };
      }
    }

    // 3. Status: Available Now
    const lower = trimmed.toLowerCase();
    const isAvailableNow =
      trimmed.includes('متاح الآن') ||
      trimmed.includes('متاح الان') ||
      trimmed === 'متاح' ||
      lower.includes('available now') ||
      lower === 'available';

    if (isAvailableNow) {
      return {
        type: 'now',
        label: isAr ? 'متاح الآن' : 'Available Now',
      };
    }

    // 4. Status: Unavailable
    const isUnavailable =
      trimmed.includes('غير متاح') ||
      trimmed.includes('غير متوفر') ||
      trimmed.includes('محجوز') ||
      lower.includes('unavailable') ||
      lower.includes('booked');

    if (isUnavailable) {
      return {
        type: 'unavailable',
        label: trimmed,
      };
    }

    // 5. Default text
    return {
      type: 'text',
      label: trimmed,
    };
  }
}
